import { useEffect, useRef, useState } from 'react';
import type { BalanceResult } from '@balance/domain';
import { api } from '../api.js';
import { eur, fmtDateTime } from '../format.js';
import { useApp } from '../state.js';

export interface Snapshot {
  version: 1;
  period: string;
  totals: Record<string, { incomeCents: number; expenseCents: number; resultCents: number; accumulatedCents: number }>;
  meters: Record<string, Record<string, { spentCents: number; budgetCents: number }>>;
  transactionIds: string[];
  takenAt: string;
}

export function takeSnapshot(balance: BalanceResult, transactionIds: string[]): Snapshot {
  const totals: Snapshot['totals'] = {};
  for (const [p, t] of Object.entries(balance.totals)) totals[p] = { incomeCents: t.incomeCents, expenseCents: t.expenseCents, resultCents: t.resultCents, accumulatedCents: t.accumulatedCents };
  const meters: Snapshot['meters'] = {};
  for (const [p, list] of Object.entries(balance.meters)) {
    meters[p] = {};
    for (const m of list) meters[p]![m.conceptId] = { spentCents: m.spentCents, budgetCents: m.budgetCents };
  }
  return { version: 1, period: balance.currentPeriod, totals, meters, transactionIds, takenAt: new Date().toISOString() };
}

export interface Change {
  label: string;
  before: number;
  after: number;
  kind: 'meter' | 'total';
  conceptId?: string;
  period: string;
}

export function diffSnapshot(prev: Snapshot, balance: BalanceResult): Change[] {
  const changes: Change[] = [];
  for (const [p, list] of Object.entries(balance.meters)) {
    for (const m of list) {
      const before = prev.meters[p]?.[m.conceptId]?.spentCents;
      if (before !== undefined && before !== m.spentCents) changes.push({ label: `${m.name} · ${p}`, before, after: m.spentCents, kind: 'meter', conceptId: m.conceptId, period: p });
    }
  }
  for (const [p, t] of Object.entries(balance.totals)) {
    const before = prev.totals[p];
    if (before && before.expenseCents !== t.expenseCents) changes.push({ label: `Egresos · ${p}`, before: before.expenseCents, after: t.expenseCents, kind: 'total', period: p });
    if (before && before.incomeCents !== t.incomeCents) changes.push({ label: `Ingresos · ${p}`, before: before.incomeCents, after: t.incomeCents, kind: 'total', period: p });
  }
  return changes;
}

/**
 * Novedades desde el último estado visto (V01–V03): el cursor visto se guarda por usuario, separado
 * de cualquier cursor de sincronización. La animación no escribe nada financiero.
 */
export function useNews() {
  const { balance, movements, userKey, reduced } = useApp();
  const [prev, setPrev] = useState<Snapshot | null | undefined>(undefined);
  const [dismissed, setDismissed] = useState(false);
  const [playing, setPlaying] = useState<{ changes: Change[]; progress: number } | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api
      .checkpoint(userKey)
      .then((c) => setPrev((c.snapshot as Snapshot | null) ?? null))
      .catch(() => setPrev(null));
  }, [userKey]);

  const changes = prev && balance ? diffSnapshot(prev, balance) : [];
  const newTx = prev && movements ? movements.transactions.filter((t) => !prev.transactionIds.includes(t.id)) : [];
  const hasNews = !dismissed && prev !== undefined && prev !== null && (changes.length > 0 || newTx.length > 0);

  async function markSeen() {
    if (!balance || !movements) return;
    const snap = takeSnapshot(balance, movements.transactions.map((t) => t.id));
    await api.saveCheckpoint(userKey, { lastSeenAt: new Date().toISOString(), snapshot: snap }).catch(() => undefined);
    setPrev(snap);
    setDismissed(true);
  }

  function play() {
    if (!balance) return;
    if (reduced) {
      void markSeen();
      return;
    }
    const start = performance.now();
    const duration = 900;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setPlaying({ changes, progress: 1 - Math.pow(1 - p, 3) });
      if (p < 1) requestAnimationFrame(tick);
      else {
        setPlaying(null);
        void markSeen();
      }
    };
    setPlaying({ changes, progress: 0 });
    requestAnimationFrame(tick);
  }

  // Primera visita: guardar el estado actual sin inventar un saldo previo cero.
  useEffect(() => {
    if (prev === null && balance && movements && !dismissed) {
      void markSeen();
    }
  }, [prev, balance, movements]); // eslint-disable-line react-hooks/exhaustive-deps

  return { hasNews, changes, newTx, playing, play, skip: markSeen, prevSeenAt: prev?.takenAt ?? null };
}

export function NewsNotice({ news }: { news: ReturnType<typeof useNews> }) {
  if (!news.hasNews) return null;
  const up = news.changes.filter((c) => c.after > c.before).length;
  const down = news.changes.filter((c) => c.after < c.before).length;
  return (
    <div className="notice" role="status">
      <div>
        <strong>Esto cambió desde tu última visita</strong>
        <div className="muted">
          {news.newTx.length} movimiento{news.newTx.length === 1 ? '' : 's'} nuevo{news.newTx.length === 1 ? '' : 's'}
          {news.newTx.length ? ` · ${eur(news.newTx.reduce((a, t) => a + t.amountCents, 0))}` : ''}
          {up ? ` · ${up} cifra${up === 1 ? '' : 's'} sube${up === 1 ? '' : 'n'}` : ''}
          {down ? ` · ${down} baja${down === 1 ? '' : 'n'} (correcciones o devoluciones)` : ''}
          {news.prevSeenAt ? ` · visto por última vez ${fmtDateTime(news.prevSeenAt)}` : ''}
        </div>
      </div>
      <div className="inline">
        <button type="button" className="button primary" onClick={news.play} disabled={!!news.playing}>
          Ver cómo se suman
        </button>
        <button type="button" className="button" onClick={() => void news.skip()}>
          Ir al estado actual
        </button>
      </div>
    </div>
  );
}
